import { useState } from 'react';
import { useWorkspaceIdFromParams } from '@/hooks/useWorkspaceIdFromParams';
import { DetailsProps } from '@components/Cards/ConnectorCard/Details';
import {
  activateConnectorCloudFunction,
  deactivateConnectorCloudFunction,
  deleteConnectorCloudFunction,
} from '@/firebase/init';
import { assert } from '@/utils/asserts';
import { useOrganizationStore } from '@/stores/organizationStore';
import { ConnectorStatus } from '@/types/Connectors';
import { showToast } from '@/utils/toasts';
import { useConnectorStateMachine } from '@/hooks/useConnectorStateMachine';
import { ConnectorCardView } from './ConnectorCardView';

interface Props extends DetailsProps {
  connectorType: string;
  connectorName: string;
  isSyncing: boolean;
  locationCode?: number;
  timezone?: string;
  status: ConnectorStatus;
  isInitialSetup: boolean;
}

export function ConnectorCard({
  connectorType,
  lastModifiedBy,
  lastModifiedDate,
  createdDate,
  createdBy,
  connectorName,
  isSyncing,
  locationCode,
  timezone,
  status,
  isInitialSetup,
}: Props) {
  const workspaceId = useWorkspaceIdFromParams();
  const [isDeactivationModalOpen, setIsDeactivationModalOpen] = useState(false);
  const [isDeletionnModalOpen, setIsDeletionnModalOpen] = useState(false);
  assert(workspaceId, 'Workspace ID is not defined');
  const selectedOrganiation = useOrganizationStore((state) => state.selectedOrganization);
  assert(selectedOrganiation, 'Organization is not defined');
  const connectorId = connectorType.replace('_', '-');
  const organizationId = selectedOrganiation.id;
  const region = selectedOrganiation.region;

  assert(organizationId, 'Organization ID is not defined');

  const [state, dispatch] = useConnectorStateMachine(
    status,
    isInitialSetup,
    isSyncing,
    organizationId,
    connectorId,
  );

  const deactivateConnector = async () => {
    setIsDeactivationModalOpen(false);
    dispatch({ type: 'DEACTIVATING', payload: { isActive: true } });
    try {
      await deactivateConnectorCloudFunction({
        connectorId: connectorType,
        workspaceId,
        organizationId,
        region,
      });
    } catch (e) {
      console.error(e);
      dispatch({ type: 'SUCCESS', payload: { isActive: true } });
      showToast('Error!', 'Failed to deactivate connector', 'error');
    }
  };

  const deleteConnector = async () => {
    setIsDeletionnModalOpen(false);
    dispatch({ type: 'DELETING', payload: { isActive: false } });
    try {
      await deleteConnectorCloudFunction({
        connectorId,
        workspaceId,
        organizationId,
        region,
      });
    } catch (e) {
      dispatch({ type: 'SUCCESS', payload: { isActive: status.includes('ACTIVATION') } });
      console.error(e);
      showToast('Error!', 'Failed to delete connector', 'error');
    }
  };

  const handleClickDelete = () => {
    setIsDeletionnModalOpen(true);
  };

  const closeDeactivationModal = () => {
    setIsDeactivationModalOpen(false);
  };

  const closeDeletionModal = () => {
    setIsDeletionnModalOpen(false);
  };

  const changeConnectorStatus = async (newStatus: boolean) => {
    try {
      if (newStatus) {
        const args = {
          connectorId: connectorType,
          workspaceId,
          organizationId,
          region,
        } as NonNullable<Parameters<typeof activateConnectorCloudFunction>[0]>;
        if (connectorId === 'dataforseo') {
          args['locationCode'] = locationCode;
          args['timezone'] = timezone;
        }
        dispatch({ type: 'ACTIVATING', payload: { isActive: false } });
        await activateConnectorCloudFunction(args);
      } else {
        setIsDeactivationModalOpen(true);
      }
    } catch (e) {
      console.error(e);
      dispatch({ type: 'SUCCESS', payload: { isActive: false } });
      showToast('Error!', 'Failed to activate connector', 'error');
    }
  };

  const disabled =
    state.isLoading || state.status === 'Syncing' || state.status === 'Failure';

  return (
    <ConnectorCardView
      changeConnectorStatus={changeConnectorStatus}
      closeDeactivationModal={closeDeactivationModal}
      closeDeletionModal={closeDeletionModal}
      connectorName={connectorName}
      connectorType={connectorType}
      createdBy={createdBy}
      createdDate={createdDate}
      lastModifiedBy={lastModifiedBy}
      lastModifiedDate={lastModifiedDate}
      state={state}
      disabled={disabled}
      workspaceId={workspaceId}
      isDeactivationModalOpen={isDeactivationModalOpen}
      isDeletionnModalOpen={isDeletionnModalOpen}
      handleClickDelete={handleClickDelete}
      deactivateConnector={deactivateConnector}
      deleteConnector={deleteConnector}
    />
  );
}
